import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useStyles } from './layered-scroll.style'

const defaultVisibleThresholds = [1, 0.99, 0.98, 0.97, 0.96]
const defaultBoxShadow = (percentIntersecting) => `0 0 10px 5px rgba(0, 0, 0, ${Math.min(percentIntersecting, 0.1) + 0.05})`

export const LayeredScrollContainer = forwardRef(function LayeredScrollContainer ({
  className,
  contentClassName,
  visibleThresholds = defaultVisibleThresholds,
  boxShadow = defaultBoxShadow,
  children,
  ...props
}, ref) {
  const styles = useStyles()

  const rootRef = useRef()
  const contentRef = useRef()

  useImperativeHandle(ref, () => rootRef.current)

  const [percentIntersecting, setPercentIntersecting] = useState()

  useEffect(() => {
    const root = rootRef.current
    const content = contentRef.current
    const observer = new IntersectionObserver(([{ intersectionRect, boundingClientRect }]) => {
      // Determine percentage of content that is intersecting (max 100%): (root.y - content.y) / content.height
      const percentIntersecting = (intersectionRect.y - boundingClientRect.y) / boundingClientRect.height
      setPercentIntersecting(percentIntersecting > 1 ? 1 : percentIntersecting)
    }, {
      root,
      threshold: visibleThresholds
    })

    observer.observe(content)

    return () => observer.unobserve(content)
  }, [visibleThresholds, setPercentIntersecting])

  return <div
    ref={rootRef}
    className={className ? `${styles.container} ${className}` : styles.container}
    {...props}
  >
    {percentIntersecting
      ? <div
        className={styles.topLayer}
        style={{ boxShadow: typeof boxShadow === 'function' ? boxShadow(percentIntersecting) : boxShadow }} />
      : null
    }
    <div
      ref={contentRef}
      className={contentClassName ? `${styles.content} ${contentClassName}` : styles.content}
    >
      {children}
    </div>
  </div>
})
